import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, FileText, Users, Calendar, MessageSquare, Ticket, X, Clock, Database, ArrowRight, CornerDownLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './GlobalSearchModal.css';

const apiUrl = process.env.REACT_APP_API_URL;

const ENTITY_ICONS = {
    User: Users,
    Users: Users,
    Article: FileText,
    Articles: FileText,
    Appointment: Calendar,
    Ticket: Ticket,
    SupportTickets: Ticket,
    ChatSession: MessageSquare,
    AwsChatSessions: MessageSquare,
};

const ENTITY_COLORS = {
    User: '#6366f1',
    Users: '#6366f1',
    Article: '#f59e0b',
    Articles: '#f59e0b',
    Appointment: '#10b981',
    Ticket: '#ef4444',
    SupportTickets: '#ef4444',
    ChatSession: '#8b5cf6',
    AwsChatSessions: '#8b5cf6',
};

const GlobalSearchModal = ({ show, onClose }) => {
    const [keyword, setKeyword] = useState('');
    const [results, setResults] = useState([]);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const [availableFilters, setAvailableFilters] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState('All');

    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (show) {
            fetchSettings();
            setTimeout(() => inputRef.current?.focus(), 120);
        } else {
            setKeyword('');
            setResults([]);
            setPage(1);
            setTotalCount(0);
            setHasMore(true);
            setSelectedFilter('All');
        }
    }, [show]);

    // Close on Escape
    useEffect(() => {
        if (!show) return;
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [show, onClose]);

    const fetchSettings = async () => {
        try {
            const res = await axios.get(`${apiUrl}/api/admin/global-search/settings`, { withCredentials: true });
            if (res.data?.Succeeded) {
                const activeTables = (res.data.Data || []).filter(t => t.IsSelected).map(t => t.TableName);
                setAvailableFilters(['All', ...activeTables]);
            }
        } catch (error) {
            console.error("Error fetching search settings for filters", error);
        }
    };

    const performSearch = async (searchStr, filterStr, pageNum, append = false) => {
        if (!searchStr.trim()) { setResults([]); setTotalCount(0); return; }
        setIsLoading(true);
        try {
            const filterParam = filterStr === 'All' ? '' : filterStr;
            const res = await axios.get(`${apiUrl}/api/admin/global-search/search`, {
                params: { keyword: searchStr, filters: filterParam, page: pageNum, pageSize: 20 },
                withCredentials: true
            });
            if (res.data?.Succeeded) {
                const data = res.data.Data;
                const newData = data.Results || [];
                setTotalCount(data.TotalCount || 0);
                setHasMore(newData.length >= 20);
                setResults(prev => append ? [...prev, ...newData] : newData);
            }
        } catch (error) {
            console.error("Error performing search", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeywordChange = (e) => {
        const val = e.target.value;
        setKeyword(val);
        setPage(1);
        setHasMore(true);
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        if (!val.trim()) { setResults([]); setTotalCount(0); return; }
        debounceTimerRef.current = setTimeout(() => {
            performSearch(val, selectedFilter, 1, false);
        }, 400);
    };

    const handleFilterClick = (filter) => {
        setSelectedFilter(filter);
        setPage(1);
        setHasMore(true);
        if (keyword.trim()) performSearch(keyword, filter, 1, false);
    };

    const handleScroll = () => {
        if (!scrollRef.current || isLoading || !hasMore) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        if (scrollHeight - scrollTop <= clientHeight + 60) {
            const nextPage = page + 1;
            setPage(nextPage);
            performSearch(keyword, selectedFilter, nextPage, true);
        }
    };

    const highlightText = (text) => {
        if (!text || !keyword) return text;
        try {
            const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`(${escaped})`, 'gi');
            const parts = String(text).split(regex);
            return parts.map((part, i) =>
                regex.test(part) ? <span key={i} className="gs-highlight">{part}</span> : part
            );
        } catch { return text; }
    };

    const handleItemClick = (item, route = null) => {
        if (route) {
            let finalPath = route.Path.replace('{id}', item.Id);
            if (finalPath === route.Path) {
                finalPath += (finalPath.includes('?') ? '&' : '?') + `id=${item.Id}`;
            }
            navigate(finalPath);
        } else if (item.RouteUrl) {
            const path = item.RouteUrl.replace('{id}', item.Id);
            navigate(path);
        } else {
            console.log("No route configured for", item);
        }
        onClose();
    };

    const getEntityIcon = (type) => {
        const Icon = ENTITY_ICONS[type] || Database;
        return Icon;
    };

    const getEntityColor = (type) => {
        return ENTITY_COLORS[type] || '#6366f1';
    };

    if (!show) return null;

    return (
        <>
            <div className="gs-overlay" onClick={onClose} />
            <div className="gs-wrapper">
                <div className="gs-modal">

                    {/* ── Search Input ── */}
                    <div className="gs-input-area">
                        <Search className="gs-search-icon" size={20} />
                        <input
                            ref={inputRef}
                            type="text"
                            className="gs-input"
                            placeholder="Search everything..."
                            value={keyword}
                            onChange={handleKeywordChange}
                        />
                        {isLoading && <div className="gs-spinner" />}
                        <span className="gs-esc-badge">ESC</span>
                        <button className="gs-close-btn" onClick={onClose}>
                            <X size={18} />
                        </button>
                    </div>

                    {/* ── Filter Pills ── */}
                    {availableFilters.length > 1 && (
                        <div className="gs-filters">
                            {availableFilters.map(f => (
                                <button
                                    key={f}
                                    onClick={() => handleFilterClick(f)}
                                    className={`gs-filter-pill ${selectedFilter === f ? 'active' : ''}`}
                                >
                                    {f}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* ── Results ── */}
                    <div className="gs-results" ref={scrollRef} onScroll={handleScroll}>

                        {/* Empty – no keyword */}
                        {results.length === 0 && !keyword.trim() && (
                            <div className="gs-empty">
                                <div className="gs-empty-icon">
                                    <Search size={24} />
                                </div>
                                <h6>Search across your entire database</h6>
                                <p>Find users, articles, tickets, and more instantly</p>
                            </div>
                        )}

                        {/* Empty – no results */}
                        {results.length === 0 && !isLoading && keyword.trim() && (
                            <div className="gs-empty">
                                <div className="gs-empty-icon">
                                    <Database size={24} />
                                </div>
                                <h6>No results found</h6>
                                <p>Nothing matches "{keyword}". Try a different keyword.</p>
                            </div>
                        )}

                        {/* Result Items */}
                        {results.map((item, idx) => {
                            const EntityIcon = getEntityIcon(item.EntityType);
                            const entityColor = getEntityColor(item.EntityType);
                            return (
                                <div key={idx} className="gs-item" onClick={() => handleItemClick(item)} role="button" tabIndex={0}>
                                    <div className="gs-item-icon" style={{
                                        background: `linear-gradient(135deg, ${entityColor}14, ${entityColor}1a)`,
                                        color: entityColor
                                    }}>
                                        <EntityIcon size={18} />
                                    </div>
                                    <div className="gs-item-body">
                                        <div className="gs-item-header">
                                            <h6 className="gs-item-title">
                                                {highlightText(item.Title)}
                                            </h6>
                                            {item.Date && (
                                                <span className="gs-item-date">
                                                    <Clock size={12} className="me-1" />
                                                    {new Date(item.Date).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                        {item.Subtitle && (
                                            <p className="gs-item-subtitle">
                                                {highlightText(item.Subtitle)}
                                            </p>
                                        )}
                                        <div className="gs-item-footer">
                                            <span className="gs-item-entity">{item.EntityType} • #{item.Id}</span>
                                        </div>
                                    </div>
                                    <div className="gs-item-action d-flex flex-column gap-1 ms-3">
                                        {(() => {
                                            let routes = [];
                                            try { if (item.RoutesJson) routes = JSON.parse(item.RoutesJson); } catch (e) {}
                                            if (routes.length > 0) {
                                                return routes.map((r, i) => (
                                                    <button 
                                                        key={i} 
                                                        className="btn btn-sm py-0 px-2 fs-10 border bg-white text-primary text-nowrap" 
                                                        onClick={(e) => { e.stopPropagation(); handleItemClick(item, r); }}
                                                    >
                                                        {r.Name} <ArrowRight size={10} className="ms-1"/>
                                                    </button>
                                                ));
                                            } else {
                                                return <CornerDownLeft size={16} className="text-muted" style={{ opacity: 0.3 }} />;
                                            }
                                        })()}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Loading More */}
                        {isLoading && results.length > 0 && (
                            <div className="gs-loading-more">
                                <div className="gs-spinner" />
                                Loading more results...
                            </div>
                        )}
                    </div>

                    {/* ── Footer ── */}
                    <div className="gs-footer">
                        <div className="gs-footer-shortcuts">
                            <span className="gs-shortcut"><kbd>↑</kbd><kbd>↓</kbd> Navigate</span>
                            <span className="gs-shortcut"><kbd>↵</kbd> Select</span>
                            <span className="gs-shortcut"><kbd>Esc</kbd> Close</span>
                        </div>
                        {totalCount > 0 && (
                            <span>{totalCount} result{totalCount !== 1 ? 's' : ''} found</span>
                        )}
                    </div>

                </div>
            </div>
        </>
    );
};

export default GlobalSearchModal;
